import React from 'react';
import { ResponsiveHeatMap } from '@nivo/heatmap';
import { generateGradientsAndColors, formatLabel, NivoTextTheme } from "./utils";

/**
 * NivoHeatMap Component
 * @param data {Array} - Heatmap data
 * @param keys {Array} - Heatmap keys for x-axis
 * @param precision {Number} - Number of decimal places for cell labels
 * @param postfix {String} - Postfix for cell labels
 * @param minValue {Number} - Minimum value for color range
 * @param maxValue {Number} - Maximum value for color range
 * @param labelSize {Number} - Font size for labels
 * @param labelColor {String} - Color for labels
 * @param showUpperAxis {Boolean} - Whether to show the upper x-axis
 * @param padLeft {Number} - Padding for the left margin
 * @returns {JSX.Element}
 * @constructor
 */
const NivoHeatMap = ({
  data,
  keys,
  precision = 0,
  postfix = '%',
  minValue = -100,
  maxValue = 100,
  labelSize = 12,
  labelColor = '#fff',
  showUpperAxis = true,
  padLeft = 30,
}) => {
  const { gradients, colorMapping } = generateGradientsAndColors(keys);
  const labelIncr = labelSize < 10 ? 2 : 0;

  return (
    <ResponsiveHeatMap
      data={data}
      keys={keys}
      indexBy="id"
      margin={{ top: showUpperAxis ? 5 : 0, right: 5, bottom: 5, left: padLeft }}
      forceSquare={true}
      axisTop={showUpperAxis ? {} : null}
      axisRight={null}
      axisBottom={null}
      opacity={0.9}
      axisLeft={{
        orient: 'left',
        tickSize: 2,
        tickPadding: 2,
        tickRotation: 0,
        legend: 'id',
        legendPosition: 'middle',
        legendOffset: -90,
      }}
      cellBorderColor={{ from: 'color', modifiers: [['brighter', 0.4]] }}
      labelTextColor={{
        from: 'color',
        modifiers: [['brighter', 2]],
      }}
      label={(d) => formatLabel(d, precision, postfix)}
      cellShape={(props) => (
        <rect
          {...props}
          style={{
            ...props.style,
            fill: `url(#gradient-${props.key})`,
          }}
        />
      )}
      defs={keys.map((key) => (
        <linearGradient
          key={key}
          id={`gradient-${key}`}
          x1="0"
          y1="0"
          x2="0"
          y2="1"
        >
          <stop offset="0%" stopColor={colorMapping[key]} stopOpacity={0.3} />
          <stop offset="100%" stopColor={colorMapping[key]} stopOpacity={1} />
        </linearGradient>
      ))}
      colors={{ type: 'diverging', scheme: 'blues', divergeAt: 0.5, minValue, maxValue }}
      legends={[
        {
          anchor: 'bottom',
          translateX: 0,
          translateY: 30,
          length: 150,
          itemHeight: 20,
          symbolSize: 10,
          direction: 'row',
          thickness: 12,
          tickPosition: 'after',
          tickSize: 0,
          tickSpacing: 4,
          tickFormat: '>-.0s',
          title: 'Value →',
          titleAlign: 'start',
          titleOffset: 4,
        },
      ]}
      animate={true}
      motionConfig="gentle"
      theme={{
        ...NivoTextTheme,
        labels: { text: { fontSize: labelSize, fontWeight: '700' } },
        axis: { ticks: { text: { fontSize: labelSize + labelIncr, fontWeight: '500' } } },
      }}
    />
  );
};

export default NivoHeatMap;

/**
 * Transform Data for Nivo HeatMap
 * @param flatData {Array} - Flattened data in the form of [{ x, y, value }]
 * @returns {Array} - Transformed data suitable for the NivoHeatMap component
 */
export const transformDataForNivoHeatMap = (flatData) => {
  const uniqueKeys = [...new Set([...flatData.map((item) => item.x), ...flatData.map((item) => item.y)])];
  const transformedData = uniqueKeys.map((keyY) => {
    const row = { id: keyY.toUpperCase(), data: [] };
    uniqueKeys.forEach((keyX) => {
      const foundItem = flatData.find((item) => item.x === keyX && item.y === keyY);
      row.data.push({ x: keyX.toUpperCase(), y: foundItem ? foundItem.value : 0 });
    });
    return row;
  });
  return transformedData;
};